import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BookCheck } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ui';
import { listMyPolicies } from '@/api/services/hrPolicies.service';
import { formatLetterDate } from '../letters/letterFormat';

/**
 * Dashboard card listing policies waiting for the viewer's acknowledgement,
 * for everyone including administrators without a self-service menu.
 * Renders nothing when there is nothing to acknowledge.
 */
export function PoliciesToAcknowledgeCard(): React.ReactElement | null {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['my-policies'],
    queryFn: listMyPolicies,
    retry: false,
  });
  const waiting = (Array.isArray(data) ? data : []).filter((policy) => policy.awaitingMe);
  if (waiting.length === 0) return null;

  return (
    <Card
      header={
        <div className="flex items-center gap-2">
          <BookCheck className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Policies to acknowledge
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">{waiting.length}</span>
        </div>
      }
      contentClassName="p-0"
    >
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {waiting.map((policy) => (
          <li
            key={policy.assignmentId}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {policy.title}
              </p>
              {policy.dueAt && (
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Acknowledge by {formatLetterDate(policy.dueAt)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {policy.overdue && <Badge variant="danger">Overdue</Badge>}
              <Button size="sm" onClick={() => navigate(`/my-policies/${policy.assignmentId}`)}>
                Read <span className="sr-only">{policy.title}</span>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
